import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Plus, AlertTriangle, CheckCircle2, AlertCircle, Calendar, FileCheck2, ExternalLink,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Card, Pill, Spinner, EmptyState } from "@/components/ui";
import { expiryBand, bandStyle, daysToExpiry, fmtDate } from "./licenceHelpers";

// =============================================================================
// InductionsScreen — /inductions
// Employee's own induction register. Sister to LicencesScreen — reuses the
// employee_licences + licence_types tables, filtered to category='induction'.
// Grouped by issuing body (Carrier / Ventia / Downer / Nokia / etc) because
// every induction type already IS a category entry. T-005.
// =============================================================================

interface InductionRow {
  licence_id:         string;
  licence_type_id:    string;
  licence_number:     string | null;
  issue_date:         string | null;
  expiry_date:        string | null;
  state_issued:       string | null;
  verified_at:        string | null;
  notes:              string | null;
  external_url:       string | null;
  front_photo_url:    string | null;
  licence_type: {
    code:                  string;
    display_name:          string;
    category:              string;
    issuing_body:          string | null;
    requires_verification: boolean;
    external_system:       string | null;
    external_system_note:  string | null;
  } | null;
}

export default function InductionsScreen() {
  const user = useRequireAuth();

  const { data: all, isLoading, error } = useQuery({
    queryKey: ["my_inductions", user?.employee_id],
    enabled: !!user?.employee_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_licences")
        .select(`
          licence_id, licence_type_id, licence_number, issue_date, expiry_date,
          state_issued, verified_at, front_photo_url, notes, external_url,
          licence_type:licence_types (
            code, display_name, category, issuing_body,
            requires_verification, external_system, external_system_note
          )
        `)
        .eq("employee_id", user!.employee_id!)
        .eq("is_active", true)
        .order("expiry_date", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as InductionRow[];
    },
  });

  // Guards -------------------------------------------------------------------
  if (!user?.employee_id) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-text-muted text-sm">
            Your login isn't linked to an employee record yet. Ask a director to complete setup.
          </div>
        </Card>
      </div>
    );
  }
  if (isLoading) {
    return <div className="min-h-[40vh] flex items-center justify-center"><Spinner size={20} /></div>;
  }
  if (error) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 flex items-start gap-3">
            <AlertTriangle size={18} className="text-danger flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium mb-1">Can't load your inductions</div>
              <div className="text-sm text-text-muted">{error instanceof Error ? error.message : "Unknown error"}</div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Filter to inductions only + group by issuing body -----------------------
  const list = (all ?? []).filter(l => l.licence_type?.category === "induction");

  const byIssuer = new Map<string, InductionRow[]>();
  for (const l of list) {
    const issuer = l.licence_type?.issuing_body ?? "Other";
    if (!byIssuer.has(issuer)) byIssuer.set(issuer, []);
    byIssuer.get(issuer)!.push(l);
  }
  const issuerOrder = Array.from(byIssuer.keys()).sort();

  const overdueCount  = list.filter(l => expiryBand(l.expiry_date) === "overdue").length;
  const warningCount  = list.filter(l => expiryBand(l.expiry_date) === "warning").length;
  const pendingVerify = list.filter(l => l.licence_type?.requires_verification && !l.verified_at).length;

  return (
    <div className="p-6 max-w-4xl mx-auto pb-24">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-1 w-10 bg-accent rounded-full" />
            <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">
              Portal · inductions
            </div>
          </div>
          <h1 className="text-2xl font-semibold">My inductions</h1>
          <p className="text-[13px] text-text-muted mt-0.5">
            Carrier WHS + tier-1 contractor inductions you hold. Click any row for evidence + external portal link.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/inductions/new">
            <Button icon={Plus}>Record induction</Button>
          </Link>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <SummaryCard label="Total" value={list.length} tone="neutral" />
        <SummaryCard label="Pending verify" value={pendingVerify} tone={pendingVerify > 0 ? "warn" : "neutral"} icon={FileCheck2} />
        <SummaryCard label="Overdue / ≤60d" value={overdueCount + warningCount} tone={overdueCount > 0 ? "danger" : warningCount > 0 ? "warn" : "neutral"} icon={AlertCircle} />
      </div>

      {list.length === 0 ? (
        <Card>
          <EmptyState
            icon={FileCheck2}
            title="No inductions recorded"
            description="Click Record induction to add your first carrier WHS or tier-1 contractor induction."
          />
        </Card>
      ) : (
        issuerOrder.map(issuer => {
          const items = byIssuer.get(issuer) ?? [];
          return (
            <div key={issuer} className="mb-5">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2 px-1">
                {issuer} ({items.length})
              </div>
              <div className="space-y-2">
                {items.map(i => <InductionRowCard key={i.licence_id} i={i} />)}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function SummaryCard({
  label, value, tone = "neutral", icon: Icon,
}: {
  label: string; value: number; tone?: "neutral" | "warn" | "danger";
  icon?: typeof AlertCircle;
}) {
  const valueColour =
    tone === "danger" ? "text-danger" :
    tone === "warn"   ? "text-warn"   :
    "text-text";
  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">{label}</div>
          {Icon && <Icon size={13} className="text-text-muted" />}
        </div>
        <div className={`text-[22px] font-semibold ${valueColour}`}>{value}</div>
      </div>
    </Card>
  );
}

function InductionRowCard({ i }: { i: InductionRow }) {
  const type = i.licence_type;
  const band = expiryBand(i.expiry_date);
  const days = daysToExpiry(i.expiry_date);
  const needsVerify = type?.requires_verification && !i.verified_at;

  return (
    <Card className={`relative overflow-hidden ${bandStyle(band)}`}>
      <Link to={`/licences/inspector?licence=${i.licence_id}`} className="block hover:bg-ink-hover/40 transition-colors">
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] text-accent">{type?.code ?? "—"}</span>
                {needsVerify && <Pill label="pending" tone="pending" small />}
                {i.verified_at && <Pill label="verified" tone="ok" small />}
              </div>
              <div className="text-[14px] font-medium text-text mt-0.5">{type?.display_name ?? "Induction"}</div>
              {type?.external_system_note && (
                <div className="text-[12px] text-text-muted mt-0.5">{type.external_system_note}</div>
              )}
              <div className="flex items-center gap-3 text-[11px] text-text-muted font-mono mt-1.5">
                {i.expiry_date ? (
                  <span className="inline-flex items-center gap-1">
                    <Calendar size={11} />
                    {fmtDate(i.expiry_date)}
                    {days != null && <span className="ml-0.5">· {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}</span>}
                  </span>
                ) : (
                  <span className="text-text-dim">no expiry</span>
                )}
                {type?.external_system && (
                  <span className="inline-flex items-center gap-1 text-accent">
                    <ExternalLink size={11} /> {type.external_system}
                  </span>
                )}
              </div>
            </div>
            {band === "overdue" && <AlertTriangle size={16} className="text-danger flex-shrink-0" />}
            {band === "warning" && <AlertTriangle size={16} className="text-warn flex-shrink-0" />}
            {!needsVerify && i.verified_at && <CheckCircle2 size={16} className="text-ok flex-shrink-0" />}
          </div>
        </div>
      </Link>
    </Card>
  );
}
